import React, { useEffect, useState } from 'react';
import { Globe, Smartphone, Monitor, Wrench, ExternalLink, Download, Apple, AlertTriangle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_APP_IMAGE = "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=400&h=300&fit=crop&q=80";

const MobileDownloadPopup = ({ open, onClose, app }) => {
    const { t } = useLanguage();

    const handleDownload = (platform) => {
        const path = platform === 'android' ? app.file_android : app.file_ios;
        if (path) window.open(`${API}/download-demo/${path}`, '_blank');
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="bg-slate-900 border-white/20 max-w-md" data-testid="download-popup-mobile">
                <DialogHeader>
                    <DialogTitle className="text-white text-xl font-bold text-center">
                        {t('T\u00e9l\u00e9charger la d\u00e9mo', 'Download demo')}
                    </DialogTitle>
                </DialogHeader>
                <p className="text-slate-400 text-sm text-center mb-6">
                    {t('Choisissez votre plateforme', 'Choose your platform')}
                </p>
                <div className="grid grid-cols-2 gap-4">
                    <Button
                        onClick={() => handleDownload('android')}
                        className="flex flex-col items-center gap-3 py-8 bg-green-600/20 hover:bg-green-600/40 border border-green-500/30 text-white h-auto"
                        data-testid="download-android"
                    >
                        <Smartphone size={32} className="text-green-400" />
                        <span className="font-bold">Android</span>
                        {app.file_android_name && (
                            <span className="text-xs text-slate-400 truncate max-w-full px-2">{app.file_android_name}</span>
                        )}
                    </Button>
                    <Button
                        onClick={() => handleDownload('ios')}
                        className="flex flex-col items-center gap-3 py-8 bg-violet-600/20 hover:bg-violet-600/40 border border-violet-500/30 text-white h-auto"
                        data-testid="download-ios"
                    >
                        <Apple size={32} className="text-violet-400" />
                        <span className="font-bold">iOS</span>
                        {app.file_ios_name && (
                            <span className="text-xs text-slate-400 truncate max-w-full px-2">{app.file_ios_name}</span>
                        )}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

const StandaloneWarningPopup = ({ open, onClose, app }) => {
    const { t } = useLanguage();

    const handleDownload = () => {
        if (app.file_standalone) {
            window.open(`${API}/download-demo/${app.file_standalone}`, '_blank');
        }
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="bg-slate-900 border-white/20 max-w-md" data-testid="download-popup-standalone">
                <DialogHeader>
                    <DialogTitle className="text-white text-xl font-bold text-center flex items-center justify-center gap-2">
                        <AlertTriangle size={22} className="text-amber-400" />
                        {t('T\u00e9l\u00e9chargement', 'Download')}
                    </DialogTitle>
                </DialogHeader>
                <p className="text-slate-300 text-sm text-center mb-2">
                    {t(
                        'Vous allez t\u00e9l\u00e9charger un fichier ex\u00e9cutable.',
                        'You are about to download an executable file.'
                    )}
                </p>
                {app.file_standalone_name && (
                    <p className="text-slate-400 text-xs text-center mb-4 font-mono bg-slate-800 rounded px-3 py-2">
                        {app.file_standalone_name}
                    </p>
                )}
                <p className="text-slate-500 text-xs text-center mb-6">
                    {t(
                        'Assurez-vous de faire confiance \u00e0 la source avant de l\u2019ex\u00e9cuter.',
                        'Make sure you trust the source before running it.'
                    )}
                </p>
                <div className="flex gap-3 justify-center">
                    <Button variant="outline" onClick={onClose} data-testid="cancel-download">
                        {t('Annuler', 'Cancel')}
                    </Button>
                    <Button onClick={handleDownload} className="bg-cyan-600 hover:bg-cyan-500" data-testid="confirm-download">
                        <Download size={16} className="mr-2" />
                        {t('T\u00e9l\u00e9charger', 'Download')}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export const Applications = () => {
    const { t, language } = useLanguage();
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterType, setFilterType] = useState('all');
    const [filterContext, setFilterContext] = useState('perso');
    const [mobilePopup, setMobilePopup] = useState({ open: false, app: null });
    const [standalonePopup, setStandalonePopup] = useState({ open: false, app: null });

    useEffect(() => {
        const fetchApplications = async () => {
            try {
                const response = await axios.get(`${API}/applications/public`);
                setApplications(response.data);
            } catch (error) {
                console.error('Error fetching applications:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchApplications();
    }, []);

    const getIcon = (type) => {
        switch (type) {
            case 'web': return <Globe className="w-6 h-6" />;
            case 'mobile': return <Smartphone className="w-6 h-6" />;
            case 'standalone': return <Monitor className="w-6 h-6" />;
            default: return <Wrench className="w-6 h-6" />;
        }
    };

    const getTypeLabel = (type) => {
        switch (type) {
            case 'web': return t('Application Web', 'Web Application');
            case 'mobile': return t('Application Mobile', 'Mobile Application');
            case 'standalone': return 'Standalone';
            default: return t('Outil', 'Tool');
        }
    };

    const getContextLabel = (contexte) => {
        switch (contexte) {
            case 'client': return t('Mission client', 'Client mission');
            case 'personnel': return t('Projet personnel', 'Personal project');
            case 'prestation': return t('Prestation', 'Service delivery');
            default: return t('Projet professionnel', 'Professional project');
        }
    };

    const getContextColor = (contexte) => {
        switch (contexte) {
            case 'client': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
            case 'personnel': return 'bg-green-500/20 text-green-400 border-green-500/30';
            case 'prestation': return 'bg-violet-500/20 text-violet-400 border-violet-500/30';
            default: return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
        }
    };

    const getDemoButtons = (app) => {
        const buttons = [];

        // Case 1: app has a lien (URL) — show link button (works for demo_type=web AND legacy apps without demo_type)
        if (app.lien) {
            buttons.push(
                <a key="link" href={app.lien} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors"
                    data-testid={`app-link-${app.id}`}>
                    <ExternalLink size={16} />
                    {t('Voir la d\u00e9mo', 'View demo')}
                </a>
            );
        }

        // Case 2: standalone JAR file
        if (app.demo_type === 'standalone' && app.file_standalone) {
            buttons.push(
                <button key="standalone" onClick={() => setStandalonePopup({ open: true, app })}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 border border-cyan-500/30 text-sm font-medium transition-colors"
                    data-testid={`demo-standalone-${app.id}`}>
                    <Download size={14} /><Monitor size={14} />{t('T\u00e9l\u00e9charger', 'Download')}
                </button>
            );
        }

        // Case 3: mobile APK + IPA
        if (app.demo_type === 'mobile' && app.file_android && app.file_ios) {
            buttons.push(
                <button key="mobile" onClick={() => setMobilePopup({ open: true, app })}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30 text-sm font-medium transition-colors"
                    data-testid={`demo-mobile-${app.id}`}>
                    <Download size={14} /><Smartphone size={14} />{t('D\u00e9mo Mobile', 'Mobile Demo')}
                </button>
            );
        }

        return buttons.length > 0 ? <div className="flex flex-wrap gap-2">{buttons}</div> : null;
    };

    const isPerso = (ctx) => ctx === 'personnel';
    const isPro = (ctx) => ctx === 'client' || ctx === 'prestation' || ctx === 'professionnel';

    const filteredApps = applications.filter(app => {
        const matchType = filterType === 'all' || app.type === filterType;
        const matchCtx = filterContext === 'all' || (filterContext === 'perso' ? isPerso(app.contexte) : isPro(app.contexte));
        return matchType && matchCtx;
    });

    const typeFilters = [
        { value: 'all', label: t('Tous', 'All') },
        { value: 'web', label: 'Web' },
        { value: 'mobile', label: 'Mobile' },
        { value: 'standalone', label: 'Standalone' },
        { value: 'outil', label: t('Outils', 'Tools') },
    ];

    const contextFilters = [
        { value: 'all', label: t('Tous', 'All') },
        { value: 'perso', label: t('Perso', 'Personal') },
        { value: 'pro', label: 'Pro' },
    ];

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <main data-testid="applications-page" className="min-h-screen pt-24">
            <section className="py-16 md:py-24">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
                    <div className="max-w-3xl">
                        <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-white mb-6">
                            Applications & {t('R\u00e9alisations', 'Projects')}
                        </h1>
                        <p className="text-lg text-slate-400">
                            {t(
                                'D\u00e9couvrez les projets et applications sur lesquels j\'ai travaill\u00e9 au cours de ma carri\u00e8re.',
                                'Discover the projects and applications I have worked on throughout my career.'
                            )}
                        </p>
                    </div>

                    <div className="space-y-3 mt-8">
                        {/* Context filter: Pro / Perso / Tous */}
                        <div className="flex flex-wrap gap-3" data-testid="context-filters">
                            {contextFilters.map(f => (
                                <button key={f.value} onClick={() => setFilterContext(f.value)}
                                    className={`px-5 py-2 rounded-full text-sm font-bold transition-colors ${
                                        filterContext === f.value ? 'bg-blue-500 text-white' : 'bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white'
                                    }`} data-testid={`filter-ctx-${f.value}`}>
                                    {f.label}
                                </button>
                            ))}
                        </div>
                        {/* Type filter: Web / Mobile / Standalone / ... */}
                        <div className="flex flex-wrap gap-3" data-testid="type-filters">
                            {typeFilters.map(f => (
                                <button key={f.value} onClick={() => setFilterType(f.value)}
                                    className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
                                        filterType === f.value ? 'bg-slate-600 text-white' : 'bg-white/5 text-slate-500 hover:bg-white/10 hover:text-white'
                                    }`} data-testid={`filter-type-${f.value}`}>
                                    {f.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="pb-24" data-testid="apps-grid">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredApps.map((app, index) => (
                            <div key={app.id}
                                className="glass-card overflow-hidden card-hover group fade-in"
                                style={{ animationDelay: `${index * 0.1}s` }}
                                data-testid={`app-${app.id}`}>
                                <div className="relative h-48 overflow-hidden bg-slate-800">
                                    <img src={app.screenshot || DEFAULT_APP_IMAGE} alt={app.nom}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                        onError={(e) => { e.target.src = DEFAULT_APP_IMAGE; }} />
                                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
                                    <div className="absolute top-3 left-3">
                                        <span className="px-2 py-1 text-xs font-bold rounded-full bg-slate-900/80 text-white backdrop-blur-sm">
                                            {getTypeLabel(app.type)}
                                        </span>
                                    </div>
                                    <div className="absolute top-3 right-3">
                                        {app.demontrable && (
                                            <span className="px-2 py-1 text-xs font-bold rounded-full bg-green-500 text-white">
                                                {t('D\u00e9mo', 'Demo')}
                                            </span>
                                        )}
                                    </div>
                                    <div className="absolute bottom-3 left-3 p-2 rounded-lg bg-blue-500/20 backdrop-blur-sm text-blue-400">
                                        {getIcon(app.type)}
                                    </div>
                                </div>

                                <div className="p-5">
                                    <h3 className="text-lg font-bold text-white mb-2 group-hover:text-blue-400 transition-colors">
                                        {app.nom}
                                    </h3>
                                    <p className="text-slate-400 text-sm mb-3 line-clamp-2">
                                        {language === 'en' && app.description_en ? app.description_en : app.description}
                                    </p>

                                    <div className="mb-3">
                                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getContextColor(app.contexte)}`}>
                                            {getContextLabel(app.contexte)}
                                        </span>
                                    </div>

                                    {app.technos?.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mb-3">
                                            {app.technos.slice(0, 4).map((tech, i) => (
                                                <span key={i} className="tech-badge text-xs">{tech}</span>
                                            ))}
                                            {app.technos.length > 4 && (
                                                <span className="text-xs text-slate-500">+{app.technos.length - 4}</span>
                                            )}
                                        </div>
                                    )}

                                    {getDemoButtons(app)}
                                </div>
                            </div>
                        ))}
                    </div>

                    {filteredApps.length === 0 && (
                        <div className="text-center py-16">
                            <p className="text-slate-400">
                                {t('Aucune application trouv\u00e9e pour ce filtre.', 'No applications found for this filter.')}
                            </p>
                        </div>
                    )}
                </div>
            </section>

            {/* Mobile download popup */}
            {mobilePopup.app && (
                <MobileDownloadPopup
                    open={mobilePopup.open}
                    onClose={() => setMobilePopup({ open: false, app: null })}
                    app={mobilePopup.app}
                />
            )}

            {/* Standalone warning popup */}
            {standalonePopup.app && (
                <StandaloneWarningPopup
                    open={standalonePopup.open}
                    onClose={() => setStandalonePopup({ open: false, app: null })}
                    app={standalonePopup.app}
                />
            )}
        </main>
    );
};
